import BookIndex from "components/shared/book/list";
import { logistTaskKanbanStages, logistTaskKanbanStagesEnum } from "helpers/enums";
import { load, store } from "helpers/filtersStorage";
import { paths } from "helpers/paths";
import { tableContainer } from "helpers/redux";
import moment from "moment";
import React from "react";
import { Link } from "react-router-dom";
import Filter from "./filter";
import KanbanTable from "./table";
import { filterItems, paginateItems } from "./utils";

const NEXT_PAGE_REQ_HEIGHT = 200;

const headerClassNames = {
    [logistTaskKanbanStagesEnum.Expired]: "col-task-kanban col-task-kanban--expired",
    [logistTaskKanbanStagesEnum.Today]: "col-task-kanban col-task-kanban--today",
    [logistTaskKanbanStagesEnum.Tomorrow]: "col-task-kanban col-task-kanban--this-week",
    [logistTaskKanbanStagesEnum.DayAfterTomorrow]: "col-task-kanban col-task-kanban--next-week",
    [logistTaskKanbanStagesEnum.Later]: "col-task-kanban col-task-kanban--later",
};

const columnClassNames = {
    [logistTaskKanbanStagesEnum.Expired]: "td row-task-kanban--expired",
    [logistTaskKanbanStagesEnum.Today]: "td row-task-kanban--today",
    [logistTaskKanbanStagesEnum.Tomorrow]: "td row-task-kanban--this-week",
    [logistTaskKanbanStagesEnum.DayAfterTomorrow]: "td row-task-kanban--next-week",
    [logistTaskKanbanStagesEnum.Later]: "td row-task-kanban--later",
};

class Tasks extends BookIndex {
    state = {
        tableColumns: this.tableColumns,
        items: {},
        initialItems: {},
        page: 1,
        loading: false,
        filters: load("kanban", {}),
    };

    get tableColumns() {
        const items = this.state?.initialItems || this.props.items;
        const columns = logistTaskKanbanStages.map((option) => {
            const { label, value: statusValue } = option;
            const count = items[statusValue]?.length ?? 0;
            return {
                Header: `${label} (${count})`,
                accessor: statusValue,
                unResize: true,
                width: 250,
                headerClassName: headerClassNames[statusValue],
                columnClassName: columnClassNames[statusValue],
                Cell: ({ value }) => {
                    if (!value) {
                        return "";
                    }
                    return value.map((cardValue) => {
                        const { id, name, userName, deadline, car, stage, daysLate, bidId, serviceWork } = cardValue;
                        return (
                            <div key={id} className="k-card">
                                <div className="k-card__content">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <a href={paths.tasksShow(id)} rel="noopener noreferrer" target="_blank">
                                            #{id}
                                        </a>
                                    </div>
                                    <div className="pb-1 font-weight-bold">{name}</div>
                                    <div className="pb-1">
                                        <div className="text-muted">Срок исполнения</div>
                                        {this.formattedDate(deadline)}
                                        {stage === logistTaskKanbanStagesEnum.Expired && (
                                            <>
                                                &nbsp;
                                                <span className="text-danger">({daysLate} дн.)</span>
                                            </>
                                        )}
                                    </div>
                                    {bidId && (
                                        <div className="pb-1">
                                            <div className="text-muted small">Заявка</div>
                                            <a
                                                href={paths.bidShow("bid", bidId)}
                                                rel="noopener noreferrer"
                                                target="_blank"
                                            >
                                                {bidId}
                                            </a>
                                        </div>
                                    )}
                                    <div className="pb-1">
                                        <div className="text-muted small">Машина</div>
                                        {car?.number}
                                    </div>
                                    <div className="pb-1">
                                        <div className="text-muted small">Ответственный</div>
                                        {userName ?? "-"}
                                    </div>
                                    {serviceWork?.type && (
                                        <div className="pb-1">
                                            <div className="text-muted small">Тип сервисной работы</div>
                                            {serviceWork?.type}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    });
                },
            };
        });
        return columns;
    }

    componentDidUpdate(prevProps) {
        if (prevProps.items !== this.props.items) {
            const filteredItems = filterItems({ ...this.props.items }, this.state.filters);
            this.setState({
                items: paginateItems(filteredItems, 1),
                page: 1,
                initialItems: filteredItems,
            });
            setTimeout(() => {
                this.setState({ tableColumns: this.tableColumns });
            }, 0);
        }
    }

    setFilter = (newValue) => {
        const newFilters = { ...this.state.filters };

        const mergeFilter = ({ name, value }) => {
            if (!value || value.length === 0) {
                delete newFilters[name];
            } else {
                newFilters[name] = value;
            }
        };

        if (Array.isArray(newValue)) {
            newValue.forEach(mergeFilter);
        } else {
            mergeFilter(newValue);
        }
        store("kanban", newFilters);
        const filteredItems = filterItems({ ...this.props.items }, newFilters);
        this.setState({ filters: newFilters, initialItems: filteredItems, items: paginateItems(filteredItems, 1) });
        setTimeout(() => {
            this.setState({ tableColumns: this.tableColumns });
        }, 0);
    };

    refreshData = () => this.props.getList();

    resetFilters = () => {
        this.setState({ filters: {} });
        setTimeout(() => {
            this.setFilter({ name: customElements, value: null });
        }, 0);
    };

    renderActions = () => (
        <Filter
            filters={this.state.filters}
            setFilter={this.setFilter}
            resetFilters={this.resetFilters}
            refreshData={this.refreshData}
        />
    );

    renderHead = () => {
        return (
            <div className="book-list__head-wrap">
                <div className="book-list__head">
                    <div className="book-list__head-close close close--sm" onClick={this.close} />
                    <div className="book-list__head-title">{this.props.title}</div>
                    <div className="btn-toolbar">
                        <div className="btn-group">
                            <Link className="btn px-4 btn-dark" to={paths.tasks}>
                                Канбан
                            </Link>
                            <Link className="btn px-4 btn-secondary" to={paths.tasksTable}>
                                Таблица
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    formattedDate = (date) => {
        const currentDateMoment = moment();
        const dateMoment = moment(date);
        if (dateMoment.year() === currentDateMoment.year()) {
            return dateMoment.format("D MMMM");
        }
        return dateMoment.format("D MMMM YYYY года");
    };

    scrollHandle = (e) => {
        if (e.target.clientHeight + e.target.scrollTop >= e.target.scrollHeight - NEXT_PAGE_REQ_HEIGHT) {
            this.setState({ loading: true });
            setTimeout(() => {
                const newPage = this.state.page + 1;
                this.setState({
                    items: paginateItems(this.state.initialItems, newPage),
                    page: newPage,
                    loading: false,
                });
            }, 0);
        }
    };

    renderListComponent = () => (
        <KanbanTable
            items={[this.state.items]}
            getList={this.props.getList}
            columns={this.state.tableColumns}
            scrollHandle={this.scrollHandle}
            loading={this.state.loading}
        />
    );
}

export default tableContainer(Tasks, "kanban-tasks");
